<template>
  <div>
    <el-alert
      title="返工把异常数量加回原工序计划。工序若已完工，会改回进行中，工人可以继续报工。同一条异常只能返工一次。"
      type="info" :closable="false" show-icon style="margin-bottom:12px"
    />
    <div class="toolbar">
      <el-input v-model="query.anomalyCode" clearable placeholder="异常单号" style="width:160px" @keyup.enter.native="search" />
      <el-input v-model="query.trackingCode" clearable placeholder="报工编号" style="width:160px" @keyup.enter.native="search" />
      <el-input v-model="query.orderCode" clearable placeholder="工单编号" style="width:160px" @keyup.enter.native="search" />
      <el-select v-model="query.anomalyState" clearable placeholder="状态" style="width:110px">
        <el-option label="草稿" value="00" />
        <el-option label="已说明" value="01" />
        <el-option label="已完成" value="02" />
      </el-select>
      <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
      <el-button type="primary" icon="el-icon-plus" @click="openEdit()">新增</el-button>
    </div>

    <el-table :data="list" border v-loading="loading">
      <el-table-column type="index" label="序号" width="60" align="center" :index="indexMethod" />
      <el-table-column prop="anomalyCode" label="异常单号" width="180" />
      <el-table-column prop="trackingCode" label="报工编号" width="170" />
      <el-table-column prop="orderCode" label="工单" width="140" />
      <el-table-column prop="productNumber" label="产品编码" width="120" />
      <el-table-column prop="productName" label="产品名称" min-width="140" show-overflow-tooltip />
      <el-table-column prop="reasonName" label="异常原因" width="120" show-overflow-tooltip />
      <el-table-column prop="quantity" label="数量" width="90" align="right" />
      <el-table-column label="状态" width="90" align="center">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="row.anomalyState === '02' ? 'success' : (row.anomalyState === '01' ? 'warning' : 'info')">
            {{ stateLabel(row.anomalyState) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="280" align="center">
        <template slot-scope="{ row }">
          <el-button type="text" :disabled="row.anomalyState !== '00'" @click="openEdit(row.anomalyId)">修改</el-button>
          <el-button type="text" :disabled="row.anomalyState !== '00'" @click="explainRow(row)">说明</el-button>
          <el-button type="text" :disabled="!!row.reworkTaskId || !(Number(row.quantity) > 0)" @click="reworkRow(row)">返工</el-button>
          <el-button type="text" :disabled="row.anomalyState !== '00' && row.anomalyState !== '01'" @click="completeRow(row)">完成</el-button>
          <el-button type="text" style="color:#ef4444" :disabled="row.anomalyState !== '00'" @click="onDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <list-pagination
      :total="total" :page="page" :page-size="pageSize" :page-sizes="[10, 20, 50, 100]"
      @current-change="onPage" @size-change="onSize"
    />

    <el-dialog :title="form.anomalyId ? '修改报工异常' : '新增报工异常'" :visible.sync="visible" width="720px" :close-on-click-modal="false">
      <el-form ref="form" :model="form" label-width="100px">
        <el-form-item label="异常单号">
          <el-input v-model="form.anomalyCode" placeholder="留空自动生成" />
        </el-form-item>
        <el-form-item label="报工单" required>
          <el-select
            v-model="form.trackingId" filterable remote :remote-method="searchTracking" :loading="trackingLoading"
            style="width:100%" @change="onTrackingChange"
          >
            <el-option
              v-for="item in trackingOptions"
              :key="item.trackingId"
              :label="(item.trackingCode || '') + ' / ' + (item.orderCode || '')"
              :value="item.trackingId"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="产品" required>
          <el-select v-model="form.productId" filterable style="width:100%">
            <el-option
              v-for="item in productOptions"
              :key="item.productId"
              :label="(item.productNumber || '') + ' / ' + (item.productName || '')"
              :value="item.productId"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="异常原因" required>
          <el-select v-model="form.reasonId" filterable style="width:100%">
            <el-option
              v-for="item in reasonOptions"
              :key="item.reasonId"
              :label="(item.reasonCode || '') + ' / ' + (item.reasonName || '')"
              :value="item.reasonId"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="数量" required>
          <el-input-number v-model="form.quantity" :min="0" :precision="5" style="width:100%" />
        </el-form-item>
        <el-form-item label="说明">
          <el-input v-model="form.explanation" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="visible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submit">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import ListPagination from '../../components/ListPagination.vue'
import {
  listTrackingAnomaly, getTrackingAnomaly, saveTrackingAnomaly, updateTrackingAnomaly,
  deleteTrackingAnomaly, explainTrackingAnomaly, completeTrackingAnomaly, reworkTrackingAnomaly
} from '../../api/trackingAnomaly'
import { listTracking } from '../../api/tracking'
import { selectAnomalyReason } from '../../api/anomalyReason'
import { selectProduct } from '../../api/product'

const STATE_MAP = { '00': '草稿', '01': '已说明', '02': '已完成' }

function emptyForm() {
  return {
    anomalyId: '', anomalyCode: '', trackingId: '', orderId: '', productId: '',
    reasonId: '', quantity: 1, explanation: '', version: 0
  }
}

export default {
  name: 'TrackingAnomalyPage',
  components: { ListPagination },
  data() {
    return {
      query: { anomalyCode: '', trackingCode: '', orderCode: '', anomalyState: '' },
      list: [], total: 0, page: 1, pageSize: 20, loading: false,
      visible: false, saving: false, trackingLoading: false, form: emptyForm(),
      trackingOptions: [], reasonOptions: [], productOptions: []
    }
  },
  created() {
    selectAnomalyReason().then(res => { this.reasonOptions = res.data || [] }).catch(() => {})
    selectProduct().then(res => { this.productOptions = res.data || [] }).catch(() => {})
    this.searchTracking('')
    this.load()
  },
  methods: {
    stateLabel(s) { return STATE_MAP[s] || s || '-' },
    indexMethod(i) { return (this.page - 1) * this.pageSize + i + 1 },
    search() { this.page = 1; this.load() },
    onPage(p) { this.page = p; this.load() },
    onSize(s) { this.pageSize = s; this.page = 1; this.load() },
    load() {
      this.loading = true
      listTrackingAnomaly({
        currentPage: this.page, pageSize: this.pageSize,
        anomalyCode: this.query.anomalyCode,
        trackingCode: this.query.trackingCode,
        orderCode: this.query.orderCode,
        anomalyState: this.query.anomalyState
      }).then(res => {
        const body = res.data || {}
        this.list = body.list || []
        this.total = body.totalCount || 0
      }).finally(() => { this.loading = false })
    },
    searchTracking(q) {
      this.trackingLoading = true
      listTracking({ currentPage: 1, pageSize: 30, trackingCode: q || '' }).then(res => {
        this.trackingOptions = ((res.data || {}).list) || []
      }).finally(() => { this.trackingLoading = false })
    },
    onTrackingChange(id) {
      const item = this.trackingOptions.find(x => x.trackingId === id)
      if (!item) {
        this.form.orderId = ''
        this.form.productId = ''
        return
      }
      this.form.orderId = item.orderId || ''
      if (item.productId) {
        this.form.productId = item.productId
        if (!this.productOptions.some(p => p.productId === item.productId)) {
          this.productOptions.push({
            productId: item.productId,
            productNumber: item.productNumber || '',
            productName: item.productName || ''
          })
        }
      }
    },
    openEdit(id) {
      this.form = emptyForm()
      this.visible = true
      this.searchTracking('')
      if (!id) return
      getTrackingAnomaly(id).then(res => {
        const d = res.data || {}
        this.form = Object.assign(emptyForm(), d)
        if (d.trackingId) {
          this.trackingOptions = [{
            trackingId: d.trackingId, trackingCode: d.trackingCode, orderCode: d.orderCode,
            orderId: d.orderId, productId: d.productId, productNumber: d.productNumber, productName: d.productName
          }]
        }
      })
    },
    submit() {
      if (!this.form.trackingId) { this.$message.warning('请选择报工单'); return }
      if (!this.form.productId) { this.$message.warning('请选择产品'); return }
      if (!this.form.reasonId) { this.$message.warning('请选择异常原因'); return }
      if (!this.form.quantity || this.form.quantity <= 0) { this.$message.warning('数量必须大于0'); return }
      this.saving = true
      const req = this.form.anomalyId ? updateTrackingAnomaly(this.form) : saveTrackingAnomaly(this.form)
      req.then(() => {
        this.$message.success('保存成功')
        this.visible = false
        this.load()
      }).finally(() => { this.saving = false })
    },
    explainRow(row) {
      this.$prompt('请输入异常说明', '说明', { inputValue: row.explanation || '' }).then(({ value }) => {
        explainTrackingAnomaly(row.anomalyId, value).then(() => {
          this.$message.success('操作成功')
          this.load()
        })
      }).catch(() => {})
    },
    reworkRow(row) {
      this.$confirm('返工不改计划数量。已完工的工序会改回进行中，便于继续报工。', '返工', { type: 'warning' }).then(() => {
        reworkTrackingAnomaly(row.anomalyId).then(() => {
          this.$message.success('已返工')
          this.load()
        })
      }).catch(() => {})
    },
    completeRow(row) {
      this.$confirm('确认完成该异常？', '提示', { type: 'warning' }).then(() => {
        completeTrackingAnomaly(row.anomalyId).then(() => {
          this.$message.success('操作成功')
          this.load()
        })
      }).catch(() => {})
    },
    onDelete(row) {
      this.$confirm('确定删除？', '提示', { type: 'warning' }).then(() => {
        deleteTrackingAnomaly([row.anomalyId]).then(() => {
          this.$message.success('已删除')
          this.load()
        })
      }).catch(() => {})
    }
  }
}
</script>
